import { useEffect, useMemo, useState } from "react";
import { Trash } from "@/components/icons";
import { Button, toast } from "@/components/kit";
import type { MarketEventStockIssueDTO } from "@/modules/event-sales/domain/types";
import {
  type PickedVariant,
  ProductPickerModal,
} from "@/modules/orders/presentation/ui/ProductPickerModal";
import { formatLak } from "@/shared/lib/currency";
import {
  useEventStockQuery,
  useIssueEventStock,
  useReturnEventStock,
  useUseEventStock,
} from "../api/queries";
import { QtyStepper } from "./touch-fields";

type EventSaleStockPanelProps = {
  eventId: string;
  canWrite: boolean;
};

type DraftLine = {
  key: string;
  variantId: string;
  productName: string;
  variantName: string;
  stockQuantity: number;
  unitLabel: string;
  price: number;
  quantity: number;
};

export function EventSaleStockPanel({
  eventId,
  canWrite,
}: EventSaleStockPanelProps) {
  const issues = useEventStockQuery(eventId);
  const issueStock = useIssueEventStock(eventId);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [lines, setLines] = useState<DraftLine[]>([]);

  const reserved = useMemo(
    () =>
      Object.fromEntries(lines.map((line) => [line.variantId, line.quantity])),
    [lines],
  );

  function pickVariant(variant: PickedVariant) {
    setLines((current) => {
      const existing = current.find(
        (line) => line.variantId === variant.variantId,
      );
      if (existing) {
        return current.map((line) =>
          line.variantId === variant.variantId
            ? {
                ...line,
                quantity: Math.min(line.stockQuantity, line.quantity + 1),
              }
            : line,
        );
      }
      return [
        ...current,
        {
          key: variant.variantId,
          variantId: variant.variantId,
          productName: variant.productName,
          variantName: variant.variantName,
          stockQuantity: variant.stockQuantity,
          unitLabel: variant.stockUnitLabel,
          price: Math.round(Number(variant.unitPrice)),
          quantity: 1,
        },
      ];
    });
    setPickerOpen(false);
  }

  async function issue() {
    if (lines.length === 0) return;
    await issueStock.mutateAsync({
      lines: lines.map((line) => ({
        variantId: line.variantId,
        quantity: line.quantity,
      })),
    });
    toast.success("ເບີກຂອງແລ້ວ");
    setLines([]);
  }

  const rows = issues.data?.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      {canWrite ? (
        <div className="brand-card flex flex-col gap-3 p-4">
          <Button
            type="button"
            variant="outline"
            className="h-12 w-full text-base"
            onClick={() => setPickerOpen(true)}
          >
            ເພີ່ມລາຍການ
          </Button>
          {lines.length > 0 ? (
            <ul className="flex flex-col gap-3">
              {lines.map((line) => (
                <li
                  key={line.key}
                  className="flex flex-col gap-2 rounded-lg border p-3"
                >
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium leading-snug">
                        {line.productName}
                      </p>
                      <p className="text-muted-foreground text-sm">
                        {line.variantName} · ຄົງເຫຼືອ {line.stockQuantity}{" "}
                        {line.unitLabel}
                      </p>
                      <p className="tabular-nums">{formatLak(line.price)}</p>
                    </div>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-11 shrink-0"
                      aria-label="ເອົາລາຍການອອກ"
                      onClick={() =>
                        setLines((current) =>
                          current.filter((item) => item.key !== line.key),
                        )
                      }
                    >
                      <Trash />
                    </Button>
                  </div>
                  <QtyStepper
                    value={line.quantity}
                    onChange={(next) =>
                      setLines((current) =>
                        current.map((item) =>
                          item.key === line.key
                            ? {
                                ...item,
                                quantity: Math.min(
                                  item.stockQuantity,
                                  Math.max(1, next),
                                ),
                              }
                            : item,
                        ),
                      )
                    }
                  />
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground text-sm">
              ເລືອກໄດ້ຫຼາຍລາຍການ ແລ້ວເບີກພ້ອມກັນ
            </p>
          )}
          <Button
            type="button"
            className="h-12 w-full text-base"
            disabled={lines.length === 0 || issueStock.isPending}
            onClick={() => void issue()}
          >
            ເບີກຈາກສາງ
          </Button>
        </div>
      ) : null}
      <ul className="flex flex-col gap-2">
        {rows.map((row) => (
          <IssueCard
            key={row.id}
            eventId={eventId}
            issue={row}
            canWrite={canWrite}
          />
        ))}
      </ul>
      {rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">ຍັງບໍ່ໄດ້ເບີກຂອງຈາກສາງ</p>
      ) : null}
      <ProductPickerModal
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onPick={pickVariant}
        excludeBundles
        localReservedByVariant={reserved}
        subtitle="ຄົ້ນສິນຄ້າ ແລ້ວເລືອກຕົວເລືອກເພື່ອເບີກ"
      />
    </div>
  );
}

function IssueCard({
  eventId,
  issue,
  canWrite,
}: {
  eventId: string;
  issue: MarketEventStockIssueDTO;
  canWrite: boolean;
}) {
  const returnStock = useReturnEventStock(eventId);
  const useStock = useUseEventStock(eventId);
  const [quantity, setQuantity] = useState(issue.outstanding);

  useEffect(() => {
    setQuantity(issue.outstanding > 0 ? issue.outstanding : 1);
  }, [issue.outstanding]);

  return (
    <li className="brand-card flex flex-col gap-3 p-4">
      <div className="flex items-start justify-between gap-3">
        <span className="font-medium leading-snug">{issue.itemName}</span>
        <span className="shrink-0 text-sm tabular-nums">
          ຄ້າງ {issue.outstanding}
        </span>
      </div>
      <p className="text-muted-foreground text-xs">
        ເບີກ {issue.quantityIssued} · ຄືນ {issue.quantityReturned} · ໃຊ້{" "}
        {issue.quantityUsed}
      </p>
      {canWrite && issue.outstanding > 0 ? (
        <div className="flex flex-col gap-2">
          <QtyStepper
            value={quantity}
            min={1}
            onChange={(next) =>
              setQuantity(Math.min(issue.outstanding, Math.max(1, next)))
            }
          />
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="outline"
              className="h-11"
              disabled={returnStock.isPending}
              onClick={() =>
                void returnStock.mutateAsync({
                  issueId: issue.id,
                  quantity,
                })
              }
            >
              ຄືນສາງ
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="h-11"
              disabled={useStock.isPending}
              onClick={() =>
                void useStock.mutateAsync({
                  issueId: issue.id,
                  quantity: issue.outstanding,
                })
              }
            >
              ໃຊ້ໝົດ
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  );
}
